import Button from "components/Button/Button";
import JobCell from "components/Card/JobCell";
import { getCompanyImageById } from "constant/jobs";
import React, { useEffect, useState } from "react";
import {
  AiOutlineAppstore,
  AiOutlineEnvironment,
  AiOutlineInfoCircle,
} from "react-icons/ai";
import { Link, useNavigate, useParams } from "react-router-dom";
import actionApi from "services/actionApi";
import { externalLink } from "utils/link";
import makeToast from "utils/toast/makeToast";

const JobExpoDetailPage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [job, setJob] = useState({});
  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    actionApi
      .jobExpoDetail(id)
      .then((response) => {
        const job = response?.data?.data;
        setJob(job);
      })
      .catch((err) => {
        makeToast({ label: err?.response?.data?.errors?.[0], type: "error" });
        navigate("/job-expo");
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const {
    companyName,
    companyID,
    positionTitle,
    industry,
    type,
    location,
    jobDescription,
    requirements,
    companyInformation,
    website,
    applicationLink,
    social,
  } = job;

  const companyImage = getCompanyImageById(companyID);

  const instagram = social?.instagram;
  const linkedin = social?.linkedin;
  const tiktok = social?.tiktok;

  return (
    <div className="bg-wce-neutral-2 w-full mx-auto relative">
      <div className="bg-wce-navy-light lg2:min-h-[208px] py-4 lg2:py-8 px-6 lg2:px-8">
        {!isLoading && positionTitle && (
          <div className="max-w-7xl flex flex-col items-center lg2:flex-row mx-auto gap-4 lg2:gap-10">
            <div className="w-[144px] min-w-[144px] hidden lg2:block">
              <img
                src={companyImage}
                alt="logo"
                className="w-[144px] min-w-[144px] h-[144px] object-contain rounded-lg bg-wce-neutral-1 p-1"
              />
            </div>
            <div className="w-full">
              <div className="flex gap-2 mb-4 lg2:mb-2 items-center">
                <div className="w-[56px] min-w-[56px] block lg2:hidden">
                  <img
                    src={companyImage}
                    alt="logo"
                    className="w-[56px] min-w-[56px] h-[56px] object-contain rounded-lg bg-wce-neutral-1 p-1"
                  />
                </div>
                <div>
                  <div className="font-medium text-sm lg2:text-xl text-wce-neutral-1 mb-1 lg2:mb-2">
                    {positionTitle}
                  </div>
                  <div className="text-wce-neutral-3 text-sm lg2:text-base">
                    {companyName}
                  </div>
                </div>
              </div>
              <div className="mb-1 lg2:mb-4 w-fit">
                <JobCell icon={AiOutlineAppstore} content={industry} />
              </div>
              <div className="flex flex-col lg2:flex-row gap-1 lg2:gap-4 w-fit lg2:whitespace-nowrap">
                <div className="w-fit">
                  <JobCell icon={AiOutlineEnvironment} content={location} />
                </div>
                <div className="w-fit">
                  <JobCell icon={AiOutlineInfoCircle} content={type} />
                </div>
              </div>
            </div>
            {applicationLink && (
              <div className="self-end w-full h-full lg2:w-fit">
                <Link to={externalLink(applicationLink)} target="_blank">
                  <Button
                    size="large"
                    color="danger"
                    rounded
                    className="mt-auto w-full lg2:w-[240px]"
                  >
                    Apply for This Job
                  </Button>
                </Link>
              </div>
            )}
          </div>
        )}
      </div>
      <div className="max-w-7xl flex flex-col mx-auto items-center px-6 mt-4 sm:mt-10 mb-14">
        {!isLoading && positionTitle && (
          <div
            className="bg-wce-neutral-1 rounded-3xl p-6 w-full shadow-wce-job
            flex flex-col gap-2 sm:gap-4"
          >
            {jobDescription && (
              <div>
                <div className="text-wce-neutral-10 text-sm sm:text-xl font-medium mb-1 sm:mb-2">
                  Job Description
                </div>
                <div className="text-wce-neutral-9 whitespace-pre-line text-sm sm:text-base">
                  <div dangerouslySetInnerHTML={{ __html: jobDescription }} />
                </div>
              </div>
            )}
            {requirements && (
              <div>
                <div className="text-wce-neutral-10 text-sm sm:text-xl font-medium mb-1 sm:mb-2">
                  Requirements
                </div>
                <div className="text-wce-neutral-9 whitespace-pre-line text-sm sm:text-base">
                  <div dangerouslySetInnerHTML={{ __html: requirements }} />
                </div>
              </div>
            )}
            {companyInformation && (
              <div>
                <div className="text-wce-neutral-10 text-sm sm:text-xl font-medium mb-1 sm:mb-2">
                  Company Information
                </div>
                <div className="text-wce-neutral-9 whitespace-pre-line text-sm sm:text-base">
                  <div
                    dangerouslySetInnerHTML={{ __html: companyInformation }}
                  />
                </div>
              </div>
            )}
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
              {website && (
                <div className="sm:min-w-[300px]">
                  <div className="text-wce-neutral-10 text-sm sm:text-xl font-medium mb-1 sm:mb-2">
                    Website
                  </div>
                  <Link
                    className="text-wce-navy-light text-sm sm:text-base"
                    to={externalLink(website)}
                    target="_blank"
                  >
                    <div className="break-all">{externalLink(website)}</div>
                  </Link>
                </div>
              )}
              {linkedin && (
                <div>
                  <div className="text-wce-neutral-10 text-sm sm:text-xl font-medium mb-1 sm:mb-2">
                    LinkedIn
                  </div>
                  <Link
                    className="text-wce-navy-light text-sm sm:text-base"
                    to={externalLink(linkedin)}
                    target="_blank"
                  >
                    <div className="break-all">{externalLink(linkedin)}</div>
                  </Link>
                </div>
              )}
            </div>
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
              {instagram && (
                <div className="sm:min-w-[300px]">
                  <div className="text-wce-neutral-10 text-sm sm:text-xl font-medium mb-1 sm:mb-2">
                    Instagram
                  </div>
                  <Link
                    className="text-wce-navy-light text-sm sm:text-base"
                    to={externalLink(instagram)}
                    target="_blank"
                  >
                    <div className="break-all">{externalLink(instagram)}</div>
                  </Link>
                </div>
              )}
              {tiktok && (
                <div>
                  <div className="text-wce-neutral-10 text-sm sm:text-xl font-medium mb-1 sm:mb-2">
                    TikTok
                  </div>
                  <Link
                    className="text-wce-navy-light text-sm sm:text-base"
                    to={externalLink(tiktok)}
                    target="_blank"
                  >
                    <div className="break-all">{externalLink(tiktok)}</div>
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default JobExpoDetailPage;
